import type { ImageSources } from '@snapchat/mw-contentful-client';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { CarouselV3AspectRatios } from '@snapchat/snap-design-system-marketing';
import { CarouselCardItem as CarouselCardItemSDS } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import type React from 'react';
import { useContext, useState } from 'react';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { shouldFlushLogs } from '../../helpers/getLocalPath';
import { logger, logUserEvent } from '../../helpers/logging';
import { useParseMediaContainer } from '../../hooks/useParseMediaContainer';
import { UserAction } from '../../types/events';
import { combineImageSources } from '../../utils/combineImageSources';
import { getFileInfo } from '../../utils/getFileInfo';
import { parseMedia } from '../../utils/media';
import { imageQuality } from '../Image';
import type { ImageDataProps } from '../Image/types';
import type { VideoDataProps } from '../Video/types';
import { getVideoWatchLogger } from '../Video/utils';
import {
  getLandscapeCardImgSrcSetSizes,
  getPortraitCardImgSrcSetSizes,
} from './CarouselV3ImageItem';
import type { CarouselV3CardItemProps } from './types';

export const CarouselV3VideoItem: FC<{
  cardItem: CarouselV3CardItemProps;
  mediaAspectRatio: CarouselV3AspectRatios;
  enableVideoControls: boolean;
  isVisible?: boolean;
  shouldLoad?: boolean;
  isResponsiveWidth?: boolean;
  hideLinkArrow?: boolean;
  className?: string;
}> = ({
  cardItem,
  mediaAspectRatio,
  enableVideoControls,
  isVisible,
  shouldLoad,
  isResponsiveWidth,
  hideLinkArrow,
  ...otherProps
}) => {
  const [videoPlayed, setVideoPlayed] = useState(false);
  let prevWatchEventTime = -1;
  const elementLocation = useContext(ElementLocation).elementLocation;

  const videoMedia = cardItem.media as VideoDataProps;

  const { getBestBgImgSrc, getImageSources } = useContentfulImages();
  const posterSource = getBestBgImgSrc(videoMedia.thumbnailMedia?.url, {
    // Double max width for the image.
    width: 728,
  });

  const {
    media: { videoSource },
    mobileMedia: { videoSource: mobileVideoSource },
  } = useParseMediaContainer(cardItem.media);

  // Logo settings
  const logoMedia = cardItem.logo as ImageDataProps | undefined;
  let logoImgSrcs: ImageSources | undefined = undefined;
  let logoAltText: string | undefined = undefined;

  if (logoMedia) {
    const { imageSource, imageAltText } = parseMedia(logoMedia.media);
    const { imageSource: mobileImageSource } = parseMedia(logoMedia.mobileMedia);
    const quality = imageQuality[logoMedia.quality ?? 'Standard'];
    const size =
      mediaAspectRatio === '9:16'
        ? getPortraitCardImgSrcSetSizes(quality)
        : getLandscapeCardImgSrcSetSizes(quality);

    logoImgSrcs = combineImageSources({
      desktop: getImageSources(imageSource, { size, quality }),
      mobile: getImageSources(mobileImageSource, { size, quality }),
    });

    logoAltText = imageAltText;
  }

  // The CarouselV3VideoItem mimics the content metrics from the Video component
  const onPlay: React.ReactEventHandler<HTMLVideoElement> = _e => {
    if (videoSource && !videoPlayed) {
      if (videoMedia.autoPlay) return; // Skip for auto play videos
      const { fileName } = getFileInfo(videoSource);

      logUserEvent({
        eventAction: UserAction.Play,
        eventCategory: 'Video',
        eventLabel: fileName,
        context: { elementLocation },
      });
      setVideoPlayed(true);
    }
  };

  const onTimeUpdate = getVideoWatchLogger({
    autoPlay: videoMedia.autoPlay,
    eventLabel: videoMedia.externalId ?? cardItem.sys.id,
    getPreviousWatchTime: () => {
      return prevWatchEventTime;
    },
    setPreviousWatchTime: value => {
      prevWatchEventTime = value;
    },
    elementLocation,
  });

  const slug = cardItem.slugReference?.slug;
  const url = slug ? `/${slug}` : cardItem.url;

  const onClick = (): void => {
    if (!url) {
      return;
    }

    logUserEvent({
      eventAction: UserAction.Click,
      eventCategory: 'CarouselV3',
      eventLabel: `CarouselV3.VideoItem - ${url}`,
      context: { targetUrl: url, elementLocation },
    });

    if (shouldFlushLogs(url)) {
      void logger.flush();
    }
  };

  return (
    <CarouselCardItemSDS
      aspectRatio={mediaAspectRatio}
      body={cardItem.body}
      showVideoControls={enableVideoControls}
      key={cardItem.sys.id}
      subtitle={cardItem.subtitle}
      title={cardItem.title}
      url={url}
      videoSource={videoSource}
      mobileVideoSource={mobileVideoSource}
      onPlay={onPlay}
      onTimeUpdate={onTimeUpdate}
      isVisible={isVisible}
      shouldLoad={shouldLoad}
      onClick={onClick}
      posterSource={posterSource}
      isResponsiveWidth={isResponsiveWidth}
      hideLinkArrow={hideLinkArrow}
      logoSrcs={logoImgSrcs}
      logoAltText={logoAltText}
      {...otherProps}
    />
  );
};

CarouselV3VideoItem.displayName = 'CarouselV3VideoItem';
